import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Phone, Coffee, Loader2, AlertCircle, ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api from '../../utils/apiClient';

/**
 * Forgot Password Page
 * Allows staff to request password reset via email or SMS
 */
export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [method, setMethod] = useState('email'); // 'email' or 'sms'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [smsCode, setSmsCode] = useState('');
  const [verifyingCode, setVerifyingCode] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      await api.post('/staff-portal/forgot-password', { email, method });

      setSent(true);
      if (method === 'email') {
        toast.success('Check your email for reset instructions');
      } else {
        toast.success('A verification code has been sent to your phone');
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Request failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (!smsCode || smsCode.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }

    setVerifyingCode(true);
    setError('');
    try {
      const res = await api.post(`/staff-portal/verify-reset-code?email=${encodeURIComponent(email)}&code=${smsCode}`);
      const data = res.data;

      toast.success('Code verified! Create your new password');
      navigate(`/staff/reset-password?token=${data.token}`);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Invalid code');
    } finally {
      setVerifyingCode(false);
    }
  };

  // SMS Code Entry Screen
  if (sent && method === 'sms') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-amber-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-600 rounded-2xl mb-4">
              <Phone className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Enter Verification Code</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              We sent a 6-digit code to your phone
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8">
            <form onSubmit={handleVerifyCode} className="space-y-5">
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  6-Digit Code
                </label>
                <Input
                  type="text"
                  value={smsCode}
                  onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="h-14 text-center text-2xl tracking-[0.5em] font-mono bg-gray-50 dark:bg-gray-700"
                  maxLength={6}
                  data-testid="sms-code-input"
                />
              </div>

              <Button
                type="submit"
                disabled={verifyingCode || smsCode.length !== 6}
                className="w-full h-12 bg-teal-600 hover:bg-teal-700 text-white"
                data-testid="verify-code-submit"
              >
                {verifyingCode ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify Code'
                )}
              </Button>

              <button
                type="button"
                onClick={() => { setSent(false); setError(''); }}
                className="w-full text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              >
                Didn't receive code? Try again
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Email Sent Success Screen
  if (sent && method === 'email') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-amber-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500 rounded-2xl mb-4">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Check Your Email</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              We've sent password reset instructions to:
            </p>
            <p className="font-medium text-gray-900 dark:text-white mt-2">{email}</p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 text-center">
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Click the link in the email to create a new password. The link will expire in 1 hour.
            </p>
            
            <div className="space-y-3">
              <button
                onClick={() => { setSent(false); setError(''); }}
                className="text-teal-600 dark:text-teal-400 hover:underline"
              >
                Didn't receive email? Try again
              </button>
              
              <div>
                <Link
                  to="/staff/login"
                  className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  ← Back to login
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main Forgot Password Form
  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-amber-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-600 rounded-2xl mb-4">
            <Coffee className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Forgot Password?</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            No worries, we'll help you reset it
          </p>
        </div>

        {/* Form */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {/* Email Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.email@example.com"
                  className="pl-10 h-12 bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600"
                  data-testid="forgot-email"
                />
              </div>
            </div>

            {/* Reset Method Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                How would you like to reset?
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setMethod('email')}
                  className={`flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-all
                    ${method === 'email' 
                      ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400' 
                      : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                    }`}
                  data-testid="method-email"
                >
                  <Mail className="w-5 h-5" />
                  <span className="font-medium">Email</span>
                </button>
                <button
                  type="button"
                  onClick={() => setMethod('sms')}
                  className={`flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-all
                    ${method === 'sms' 
                      ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400' 
                      : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                    }`}
                  data-testid="method-sms"
                >
                  <Phone className="w-5 h-5" />
                  <span className="font-medium">SMS</span>
                </button>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                {method === 'email' 
                  ? "We'll send a reset link to your email" 
                  : "We'll text a verification code to your phone on file"
                }
              </p>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-teal-600 hover:bg-teal-700 text-white"
              data-testid="forgot-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Reset Instructions'
              )}
            </Button>
          </form>

          {/* Back to Login */}
          <div className="mt-6 text-center">
            <Link
              to="/staff/login"
              className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
